import React, { useState, useEffect } from 'react';
import {
  IconAddressBook,
  IconMessage,
  IconRecordMail,
  IconPhone,
} from '@tabler/icons-react';
import SidebarItem from './SidebarItem.tsx';
import UserDropdown from './UserDropdown.tsx';
import { TendrilLogoColorTyped } from '@assets/index.ts';
import { Settings2, Settings, CalendarDays, LayoutDashboard, Wand2 } from 'lucide-react';
import { privateRoutes } from '@constants';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ENV } from '@constants/env.ts';
import { EnvVersionBanner } from '@ui/shared/envVersionBanner.tsx';
import { getCurrentUser } from '@features/auth';
import { useFeatureFlagsStore } from '@store/featureFlags';
import { useWizardStore } from '@features/wizard';
interface SidebarProps {
  isOpen: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = getCurrentUser();
  const isAdmin = currentUser?.isAdmin;
  const featureFlags = useFeatureFlagsStore((s) => s.flags);
  const isSessionStarted = useWizardStore((s) => s.isSessionStarted);

  const organization = {
    avatar: TendrilLogoColorTyped,
    name: 'Tendril Connect',
  };

  const labelFromPath = (pathname: string): string => {
    if (pathname.includes(privateRoutes.WIZARD)) {
      return t('sidebar.wizard');
    }
    if (pathname.includes(privateRoutes.CONTACTS)) {
      return t('sidebar.contacts');
    }
    if (pathname.includes(privateRoutes.VOICEMAIL)) {
      return t('sidebar.voicemail');
    }
    if (pathname.includes(privateRoutes.INTENT)) {
      return t('sidebar.intent');
    }
    if (pathname.includes(privateRoutes.DASHBOARD_SESSIONS)) {
      return t('sidebar.dashboard');
    }
    if (pathname.includes(privateRoutes.CALENDAR)) {
      return t('sidebar.calendar');
    }
    if (pathname.includes(privateRoutes.ADMINCONSOLE)) {
      return t('sidebar.adminConsole');
    }
    if (pathname.includes(privateRoutes.NUMBERS)) {
      return t('sidebar.numbers');
    }
    if (pathname.includes(privateRoutes.SETTINGS)) {
      return t('sidebar.settings');
    }
    return t('sidebar.home');
  };

  const [selectedLabel, setSelectedLabel] = useState(() =>
    labelFromPath(location.pathname),
  );

  useEffect(() => {
    setSelectedLabel(labelFromPath(location.pathname));
  }, [location.pathname, t]);

  return (
    <aside
      className={`${
        isOpen ? 'w-64' : 'w-16'
      } bg-sidebar text-sidebar-foreground dark:bg-sidebar dark:text-sidebar-foreground
         flex flex-col justify-between p-4 border-r border-border transition-all duration-300
          ${isSessionStarted ? 'opacity-70 cursor-not-allowed select-none' : ''}`}
    >
      <div className={`${isSessionStarted ? 'pointer-events-none' : ''}`}>
        <div className="mb-4 pb-4 border-b border-border">
          <div
            className={`flex items-center gap-3 justify-${isOpen ? 'center' : 'center'}`}
          >
            <img
              src={organization.avatar}
              alt="Logo"
              className={`w-9 h-9 ${!isOpen && 'mx-auto'}`}
            />
            {isOpen && (
              <span className="font-semibold">{organization.name}</span>
            )}
          </div>
        </div>

        <nav className="space-y-2">
          {/* <SidebarItem
            icon={<House size={20} data-qa-id="home-tab" />}
            label={t('sidebar.home')}
            onClick={() => {
              navigate(`/${privateRoutes.HOME}`);
            }}
            isSidebarOpen={isOpen}
            selectedLabel={selectedLabel}
            onSelect={setSelectedLabel}
          /> */}
          <SidebarItem
            icon={<Wand2 size={20} data-qa-id="wizard-tab" />}
            label={t('sidebar.wizard')}
            onClick={() => {
              navigate(`/${privateRoutes.WIZARD}`);
            }}
            isSidebarOpen={isOpen}
            selectedLabel={selectedLabel}
            onSelect={setSelectedLabel}
            disabled={isSessionStarted}
          />
          <SidebarItem
            icon={<IconMessage size={18} data-qa-id="intent-tab" />}
            label={t('sidebar.intent')}
            onClick={() => {
              navigate(`/${privateRoutes.INTENT}`);
            }}
            isSidebarOpen={isOpen}
            selectedLabel={selectedLabel}
            onSelect={setSelectedLabel}
            disabled={isSessionStarted}
          />
          <SidebarItem
            icon={<IconAddressBook size={22} data-qa-id="contacts-tab" />}
            label={t('sidebar.contacts')}
            onClick={() => {
              navigate(`/${privateRoutes.CONTACTS}`);
            }}
            isSidebarOpen={isOpen}
            selectedLabel={selectedLabel}
            onSelect={setSelectedLabel}
            disabled={isSessionStarted}
          />
          <SidebarItem
            icon={<IconRecordMail size={22} data-qa-id="voicemail-tab" />}
            label={t('sidebar.voicemail')}
            onClick={() => {
              navigate(`/${privateRoutes.VOICEMAIL}`);
            }}
            isSidebarOpen={isOpen}
            selectedLabel={selectedLabel}
            onSelect={setSelectedLabel}
            disabled={isSessionStarted}
          />
          <SidebarItem
            icon={<IconPhone size={18} data-qa-id="numbers-tab" />}
            label={t('sidebar.numbers')}
            onClick={() => {
              navigate(`/${privateRoutes.NUMBERS}`);
            }}
            isSidebarOpen={isOpen}
            selectedLabel={selectedLabel}
            onSelect={setSelectedLabel}
            disabled={isSessionStarted}
          />
          <SidebarItem
            icon={<LayoutDashboard size={18} data-qa-id="dashboard-tab" />}
            label={t('sidebar.dashboard')}
            onClick={() => {
              navigate(`/${privateRoutes.DASHBOARD_SESSIONS}`);
            }}
            isSidebarOpen={isOpen}
            selectedLabel={selectedLabel}
            onSelect={setSelectedLabel}
            disabled={isSessionStarted}
          />
          {featureFlags.calendly && (
            <SidebarItem
              icon={<CalendarDays size={18} data-qa-id="calendar-tab" />}
              label={t('sidebar.calendar')}
              onClick={() => {
                navigate(`/${privateRoutes.CALENDAR}`);
              }}
              isSidebarOpen={isOpen}
              selectedLabel={selectedLabel}
              onSelect={setSelectedLabel}
              disabled={isSessionStarted}
            />
          )}
          {(isAdmin === true) && (
            <>
              <SidebarItem
                icon={<Settings2 size={18} data-qa-id="admin-console-tab" />}
                label={t('sidebar.adminConsole')}
                onClick={() => {
                  navigate(`/${privateRoutes.ADMINCONSOLE}`);
                }}
                isSidebarOpen={isOpen}
                selectedLabel={selectedLabel}
                onSelect={setSelectedLabel}
                disabled={isSessionStarted}
              />
            </>
          )}
          <SidebarItem
            icon={<Settings size={18} data-qa-id="settings-tab" />}
            label={t('sidebar.settings')}
            onClick={() => {
              navigate(`/${privateRoutes.SETTINGS}`);
            }}
            isSidebarOpen={isOpen}
            selectedLabel={selectedLabel}
            onSelect={setSelectedLabel}
            disabled={isSessionStarted}
          />
        </nav>
      </div>

      <div className="pt-1">
        <UserDropdown isOpen={isOpen} />
        {ENV === 'prod' ? <EnvVersionBanner isOpen={isOpen}/> : null}
      </div>
    </aside>
  );
};

export default Sidebar;
